/**
 * AuthScreen, přihlášení / registrace / obnova hesla.
 * Zobrazí se na vyžádání (anonymní uživatel klikne „Přihlásit se") nebo
 * když akce vyžaduje účet. `onBack` vrátí zpět k anonymní kalkulačce.
 */
import React, { useState } from 'react';
import { useAuth } from './AuthProvider';

const wrap = {
  minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: '#F4F2F8', padding: '1rem',
};
const card = {
  background: '#FFFFFF', border: '1px solid rgba(0,0,0, 0.18)', borderRadius: 16,
  padding: '2.5rem 2.25rem', maxWidth: 420, width: '100%', position: 'relative', zIndex: 1,
  fontFamily: "'Inter', sans-serif",
};
const input = {
  width: '100%', padding: '0.8rem 1rem', border: '1px solid rgba(155,89,212,0.25)',
  borderRadius: 10, fontSize: '1rem', outline: 'none', boxSizing: 'border-box',
  marginBottom: '0.75rem', background: '#FFFFFF', color: '#1A1528',
};
const button = {
  width: '100%', padding: '0.85rem', background: '#6B3FA0',
  color: '#FFFFFF', border: 'none', borderRadius: 10, fontSize: '0.85rem', fontWeight: 700,
  cursor: 'pointer', letterSpacing: '0.06em', textTransform: 'uppercase',
};
const linkBtn = {
  background: 'transparent', border: 'none', color: '#A07B2D', cursor: 'pointer',
  fontSize: '0.8rem', padding: 0, textDecoration: 'underline', fontFamily: "'Inter', sans-serif",
};

export function AuthScreen({ onBack, initialMode = 'login', intro }) {
  const { signIn, signUp, resetPassword } = useAuth();
  const [mode, setMode] = useState(initialMode); // login | register | reset
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [company, setCompany] = useState('');
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setMsg(null);
    try {
      if (mode === 'login') {
        const { error } = await signIn(email, password); if (error) throw error;
      } else if (mode === 'register') {
        const { error } = await signUp(email, password, { full_name: fullName, company_name: company });
        if (error) throw error;
        setMsg({ ok: true, text: 'Účet vytvořen, jste přihlášeni.' });
      } else {
        const { error } = await resetPassword(email); if (error) throw error;
        setMsg({ ok: true, text: 'Odeslali jsme odkaz pro obnovu hesla (pokud je e-mail nastaven).' });
      }
    } catch (err) {
      setMsg({ ok: false, text: err.message || 'Něco se pokazilo.' });
    } finally { setBusy(false); }
  };

  return (
    <div style={wrap}>
      <form onSubmit={submit} style={card}>
        <div style={{ fontFamily: "'Source Serif 4', Georgia, serif", fontSize: '1.5rem', marginBottom: '0.35rem', color: '#1A1528' }}>
          M<span style={{ color: '#A07B2D' }}>&amp;</span>Ai
        </div>
        <h2 style={{ margin: '0 0 0.35rem', fontSize: '1.2rem', color: '#1A1528', fontWeight: 600 }}>
          {mode === 'login' && 'Přihlášení'}
          {mode === 'register' && 'Vytvořit účet'}
          {mode === 'reset' && 'Obnova hesla'}
        </h2>
        <p style={{ color: 'rgba(0,0,0, 0.6)', fontSize: '0.85rem', marginBottom: '1.5rem' }}>
          {intro || 'Valuace e-commerce, vše na jednom místě.'}
        </p>

        {mode === 'register' && (
          <>
            <input style={input} placeholder="Jméno a příjmení" value={fullName} onChange={e => setFullName(e.target.value)} />
            <input style={input} placeholder="Firma (volitelné)" value={company} onChange={e => setCompany(e.target.value)} />
          </>
        )}
        <input style={input} type="email" placeholder="E-mail" value={email} autoFocus onChange={e => setEmail(e.target.value)} required />
        {mode !== 'reset' && (
          <input style={input} type="password" placeholder="Heslo" value={password} onChange={e => setPassword(e.target.value)} required minLength={6} />
        )}

        {msg && <div style={{ color: msg.ok ? '#86efac' : '#fca5a5', fontSize: '0.82rem', marginBottom: '0.75rem' }}>{msg.text}</div>}

        <button type="submit" style={{ ...button, opacity: busy ? 0.6 : 1 }} disabled={busy}>
          {busy ? '…' : mode === 'login' ? 'Vstoupit' : mode === 'register' ? 'Vytvořit účet' : 'Odeslat odkaz'}
        </button>

        <div style={{ marginTop: '1.25rem', fontSize: '0.8rem', display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
          {mode !== 'login' && <button type="button" onClick={() => { setMode('login'); setMsg(null); }} style={linkBtn}>Přihlásit se</button>}
          {mode !== 'register' && <button type="button" onClick={() => { setMode('register'); setMsg(null); }} style={linkBtn}>Vytvořit účet</button>}
          {mode !== 'reset' && <button type="button" onClick={() => { setMode('reset'); setMsg(null); }} style={linkBtn}>Zapomenuté heslo</button>}
        </div>

        {onBack && (
          <div style={{ marginTop: '1rem', textAlign: 'center' }}>
            <button type="button" onClick={onBack} style={{ ...linkBtn, color: 'rgba(0,0,0,0.6)' }}>← Zpět na kalkulačku</button>
          </div>
        )}
      </form>
    </div>
  );
}
